/** Rewards service client — Family Coins, catalogue, redemptions, earning rules. */
import { api } from './client';

export interface CoinAccount {
  child_id: string;
  balance: number;
  lifetime_earned: number;
}

export interface CoinTransaction {
  id: string;
  child_id: string;
  amount: number;
  reason: string;
  note: string | null;
  created_at: string;
}

export interface RewardItem {
  id: string;
  family_id: string;
  name: string;
  cost_coins: number;
  icon: string | null;
  effect: string | null;
  active: boolean;
  sort_order: number;
}

export interface Redemption {
  id: string;
  child_id: string;
  reward_item_id: string;
  reward_name: string;
  cost_coins: number;
  status: 'pending' | 'fulfilled' | 'denied';
  requested_at: string;
  decided_at: string | null;
}

export type EarnReason =
  | 'daily_routine'
  | 'screen_time_respected'
  | 'focus_completed'
  | 'bedtime_followed'
  | 'arrived_home'
  | 'chore'
  | 'safety_lesson';

export interface EarningRule {
  reason: EarnReason;
  coins: number;
  enabled: boolean;
}

export const rewardsApi = {
  account: (childId: string) =>
    api<CoinAccount>(`/v1/rewards/children/${childId}/account`),
  ledger: (childId: string, limit = 50) =>
    api<CoinTransaction[]>(`/v1/rewards/children/${childId}/ledger?limit=${limit}`),
  award: (childId: string, input: { reason: EarnReason; coins?: number; note?: string }) =>
    api<CoinAccount>(`/v1/rewards/children/${childId}/award`, { method: 'POST', body: input }),
  catalogue: () => api<RewardItem[]>('/v1/rewards/catalogue'),
  createReward: (input: { name: string; cost_coins: number; icon?: string; effect?: string; sort_order?: number }) =>
    api<RewardItem>('/v1/rewards/catalogue', { method: 'POST', body: input }),
  updateReward: (id: string, input: Partial<Pick<RewardItem, 'name' | 'cost_coins' | 'icon' | 'effect' | 'active' | 'sort_order'>>) =>
    api<RewardItem>(`/v1/rewards/catalogue/${id}`, { method: 'PATCH', body: input }),
  deleteReward: (id: string) =>
    api<void>(`/v1/rewards/catalogue/${id}`, { method: 'DELETE' }),
  redemptions: (pendingOnly = true) =>
    api<Redemption[]>(`/v1/rewards/redemptions?pending_only=${pendingOnly}`),
  decideRedemption: (id: string, fulfill: boolean) =>
    api<Redemption>(`/v1/rewards/redemptions/${id}/decide`, { method: 'POST', body: { fulfill } }),
  earningRules: () => api<EarningRule[]>('/v1/rewards/earning-rules'),
  setEarningRule: (input: EarningRule) =>
    api<EarningRule>('/v1/rewards/earning-rules', { method: 'PUT', body: input }),
};
